import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { MapContainer } from '../components/map/MapProvider';
import MapPreviewCard from '../components/map/MapPreviewCard';
import MapLayerControls from '../components/map/MapLayerControls';
import PlaceView from '../components/map/PlaceView';
import {
  ArrowLeft, Search, X, Locate, RefreshCw, Plus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MapView() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Map state
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Layer state
  const [activeLayer, setActiveLayer] = useState('nearby');
  const [activeOverlays, setActiveOverlays] = useState([]);
  const [showLayerControls, setShowLayerControls] = useState(false);

  // Interaction state
  const [selectedPin, setSelectedPin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showSearchArea, setShowSearchArea] = useState(false);
  const [viewport, setViewport] = useState(null);

  // Settings
  const [mapSettings, setMapSettings] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedAnchor, setSelectedAnchor] = useState(null);
  const [timeFilter, setTimeFilter] = useState(null);
  const hasMoved = useRef(false);

  // Load map settings + anchors on mount
  useEffect(() => {
    loadSettings();
    axios.get(`${API}/api/map/anchors`).then(r => setAnchors(r.data.anchors || [])).catch(() => {});
    axios.get(`${API}/api/map/tags`).then(r => setTags(r.data.tags || [])).catch(() => {});
  }, []);

  // Load pins when layer/overlays/tag/timeFilter change
  useEffect(() => {
    if (center) loadPins();
  }, [activeLayer, activeOverlays, center, selectedTag, timeFilter]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const res = await axios.get(`${API}/api/map/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const settings = res.data;
        setMapSettings(settings);

        if (settings.default_layer) setActiveLayer(settings.default_layer);
        if (settings.default_overlays) setActiveOverlays(settings.default_overlays);

        // Set center: last viewed > travel > home > default
        if (settings.last_viewed?.lat) {
          setCenter([settings.last_viewed.lat, settings.last_viewed.lng]);
        } else if (settings.travel_mode && settings.travel_location?.lat) {
          setCenter([settings.travel_location.lat, settings.travel_location.lng]);
        } else if (settings.home_location?.lat) {
          setCenter([settings.home_location.lat, settings.home_location.lng]);
        } else {
          setCenter([44.4759, -73.2121]); // Default: Burlington, VT
        }
      } else {
        // Guest: use last viewed from localStorage, else default to Burlington (seeded area)
        const saved = localStorage.getItem('lk_map_center');
        if (saved) {
          try {
            const { lat, lng } = JSON.parse(saved);
            if (lat && lng) { setCenter([lat, lng]); return; }
          } catch {}
        }
        setCenter([44.4759, -73.2121]); // Default: Burlington, VT
      }
    } catch {
      setCenter([44.4759, -73.2121]); // Default: Burlington, VT
    } finally {
      setLoading(false);
    }
  };

  const loadPins = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ layer: activeLayer, limit: '200' });
      if (activeOverlays.length > 0) {
        params.set('overlays', activeOverlays.join(','));
      }
      if (selectedTag) {
        params.set('tag', selectedTag);
      }
      if (timeFilter) {
        params.set('time_filter', timeFilter);
      }
      if (viewport?.bounds) {
        params.set('north', viewport.bounds.north);
        params.set('south', viewport.bounds.south);
        params.set('east', viewport.bounds.east);
        params.set('west', viewport.bounds.west);
      }
      const res = await axios.get(`${API}/api/map/pins?${params}`, { headers });
      setPins(res.data.pins || []);
    } catch { /* ignore */ }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Geocode → recenter map
      const geoRes = await axios.get(`${API}/api/map/geocode?q=${encodeURIComponent(searchQuery)}`, { headers }).catch(() => null);
      if (geoRes?.data?.lat && geoRes?.data?.lng) {
        setCenter([geoRes.data.lat, geoRes.data.lng]);
      }
      
      // Reload pins for new area (use geocoded center bounds or current)
      setTimeout(() => loadPins(), 500);
    } catch { /* ignore */ }
  };

  const handlePinClick = (pin) => {
    setSelectedAnchor(null);
    setSelectedPin(pin);
  };

  const handleAnchorClick = (anchor) => {
    setSelectedPin(null);
    setSelectedAnchor(anchor);
  };

  const handleMapClick = () => {
    setSelectedPin(null);
    setSelectedAnchor(null);
  };

  const handleMoveEnd = useCallback((vp) => {
    setViewport(vp);
    if (hasMoved.current) {
      setShowSearchArea(true);
      // Persist last viewed for returning users
      if (vp?.center && user) {
        const token = localStorage.getItem('token');
        axios.put(`${API}/api/map/settings`,
          { last_viewed: { lat: vp.center.lat, lng: vp.center.lng } },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
      // Also store in localStorage for guests
      if (vp?.center) {
        localStorage.setItem('lk_map_center', JSON.stringify({ lat: vp.center.lat, lng: vp.center.lng }));
      }
    }
    hasMoved.current = true;
  }, [user]);

  const handleSearchThisArea = () => {
    setShowSearchArea(false);
    loadPins();
    // Save last viewed
    if (viewport?.center && user) {
      const token = localStorage.getItem('token');
      axios.put(`${API}/api/map/settings`,
        { last_viewed: { lat: viewport.center.lat, lng: viewport.center.lng } },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }
  };

  const handlePreviewAction = () => {
    if (!selectedPin) return;
    const type = selectedPin.type;
    const id = selectedPin.id;
    if (type === 'activity') navigate(`/activities/${id}`);
    else if (type === 'group') navigate(`/group/${id}`);
    else if (type === 'commons') navigate(`/commons/${id}`);
    else if (type === 'institution') navigate(`/institution/${id}`);
  };

  const handleLayerChange = (layer) => {
    setActiveLayer(layer);
    setShowLayerControls(false);
    // Save preference
    if (user) {
      const token = localStorage.getItem('token');
      axios.put(`${API}/api/map/settings`,
        { default_layer: layer },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }
  };

  const handleOverlayToggle = (overlayId) => {
    setActiveOverlays(prev => {
      const next = prev.includes(overlayId)
        ? prev.filter(o => o !== overlayId)
        : [...prev, overlayId];
      // Save preference
      if (user) {
        const token = localStorage.getItem('token');
        axios.put(`${API}/api/map/settings`,
          { default_overlays: next },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="h-screen relative overflow-hidden" data-testid="map-view">
      {/* Map fills the viewport */}
      <MapContainer
        center={center}
        zoom={zoom}
        pins={pins}
        highlightedPinId={selectedPin?.id}
        onPinClick={handlePinClick}
        onAnchorClick={handleAnchorClick}
        onMapClick={handleMapClick}
        onMoveEnd={handleMoveEnd}
        anchors={anchors}
        style={{ width: '100%', height: '100%' }}
      />

      {/* Top bar — floating over map */}
      <div className="absolute top-0 left-0 right-0 z-[1000] p-3 pointer-events-none">
        <div className="max-w-2xl mx-auto flex items-center gap-2 pointer-events-auto">
          {/* Back / Home button */}
          <button
            onClick={() => navigate('/base-camp')}
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-md"
            style={{ backgroundColor: 'var(--fy-surface-card)' }}
            data-testid="map-back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>

          {/* Search bar */}
          {showSearch ? (
            <div className="flex-1 flex items-center gap-2 px-3 h-10 rounded-full shadow-md"
              style={{ backgroundColor: 'var(--fy-surface-card)' }}>
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-secondary)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by city, ZIP, or activity..."
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: 'var(--fy-text-primary)' }}
                autoFocus
                data-testid="map-search-input"
              />
              <button onClick={() => { setShowSearch(false); setSearchQuery(''); loadPins(); }}>
                <X className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowSearch(true)}
              className="flex-1 flex items-center gap-2 px-4 h-10 rounded-full shadow-md"
              style={{ backgroundColor: 'var(--fy-surface-card)' }}
              data-testid="map-search-btn"
            >
              <Search className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                What's happening around me?
              </span>
            </button>
          )}

          {/* Layer button */}
          <button
            onClick={() => setShowLayerControls(prev => !prev)}
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-md"
            style={{
              backgroundColor: showLayerControls ? 'var(--lk-sage)' : 'var(--fy-surface-card)',
              color: showLayerControls ? 'white' : 'var(--fy-text-primary)'
            }}
            data-testid="map-layers-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tag filter pills + time filter — below search bar */}
      {tags.length > 0 && (
        <div className="absolute top-16 left-0 right-0 z-[999] px-3 pointer-events-none">
          <div className="max-w-2xl mx-auto space-y-1.5 py-1">
            {/* Tag row */}
            <div className="flex gap-1.5 overflow-x-auto pointer-events-auto" style={{ scrollbarWidth: 'none' }}>
              {tags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => setSelectedTag(selectedTag === tag.id ? null : tag.id)}
                  className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium shadow-sm transition-colors"
                  style={{
                    backgroundColor: selectedTag === tag.id ? 'var(--lk-sage)' : 'var(--fy-surface-card)',
                    color: selectedTag === tag.id ? '#fff' : 'var(--fy-text-secondary)',
                    border: `1px solid ${selectedTag === tag.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                  }}
                  data-testid={`tag-${tag.id}`}
                >
                  {tag.label}
                </button>
              ))}
            </div>
            {/* Time filter row */}
            <div className="flex gap-1.5 pointer-events-auto">
              {[
                { id: 'tonight', label: 'Tonight' },
                { id: 'today', label: 'Today' },
                { id: 'week', label: 'This week' },
              ].map(tf => (
                <button
                  key={tf.id}
                  onClick={() => setTimeFilter(timeFilter === tf.id ? null : tf.id)}
                  className="px-3 py-1 rounded-full text-xs font-medium shadow-sm transition-colors"
                  style={{
                    backgroundColor: timeFilter === tf.id ? 'var(--lk-sage)' : 'var(--fy-surface-card)',
                    color: timeFilter === tf.id ? '#fff' : 'var(--fy-text-secondary)',
                    border: `1px solid ${timeFilter === tf.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                  }}
                  data-testid={`time-${tf.id}`}
                >
                  {tf.label}
                </button>
              ))}
              {(selectedTag || timeFilter) && (
                <button
                  onClick={() => { setSelectedTag(null); setTimeFilter(null); }}
                  className="px-2 py-1 rounded-full text-xs font-medium pointer-events-auto"
                  style={{ color: 'var(--fy-text-disabled)' }}
                  data-testid="clear-filters"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Layer controls drawer */}
      {showLayerControls && (
        <MapLayerControls
          activeLayer={activeLayer}
          activeOverlays={activeOverlays}
          onLayerChange={handleLayerChange}
          onOverlayToggle={handleOverlayToggle}
          onClose={() => setShowLayerControls(false)}
        />
      )}

      {/* "Search this area" pill — below filter rows */}
      {showSearchArea && !selectedPin && !selectedAnchor && (
        <div className="absolute top-32 left-0 right-0 z-[1000] flex justify-center pointer-events-none">
          <button
            onClick={handleSearchThisArea}
            className="px-4 py-2 rounded-full text-sm font-medium shadow-lg pointer-events-auto flex items-center gap-2"
            style={{ backgroundColor: 'var(--fy-surface-card)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="search-this-area-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Search this area
          </button>
        </div>
      )}

      {/* Preview card */}
      {selectedPin && (
        <MapPreviewCard
          pin={selectedPin}
          onAction={handlePreviewAction}
          onDismiss={() => setSelectedPin(null)}
          isGuest={!user}
        />
      )}

      {/* Place View — lightweight anchor profile */}
      {selectedAnchor && (
        <PlaceView
          anchor={selectedAnchor}
          onClose={() => setSelectedAnchor(null)}
        />
      )}

      {/* Floating Create Activity button */}
      <div className="absolute bottom-6 right-4 z-[2000] pointer-events-auto" data-testid="map-create-fab"
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const params = new URLSearchParams();
            if (viewport?.center) {
              params.set('lat', viewport.center.lat.toFixed(4));
              params.set('lng', viewport.center.lng.toFixed(4));
            }
            navigate(`/quick-create?${params}`);
          }}
          className="flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-lg transition-transform active:scale-95"
          style={{ backgroundColor: 'var(--lk-deep-moss, #4A5A3A)', color: '#fff' }}
          data-testid="create-activity-fab"
        >
          <Plus className="w-5 h-5" />
          Start something
        </button>
      </div>

      {/* Empty state — when no pins visible */}
      {pins.length === 0 && !loading && (
        <div className="absolute inset-0 z-[500] flex items-center justify-center pointer-events-none">
          <div
            className="pointer-events-auto text-center p-6 rounded-2xl"
            style={{ backgroundColor: 'var(--fy-surface-card)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid var(--fy-border-subtle)' }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Nothing here yet</p>
            <p className="text-xs mb-3" style={{ color: 'var(--fy-text-disabled)' }}>Be the first to start something here.</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const params = new URLSearchParams();
                if (viewport?.center) {
                  params.set('lat', viewport.center.lat.toFixed(4));
                  params.set('lng', viewport.center.lng.toFixed(4));
                }
                navigate(`/quick-create?${params}`);
              }}
              className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="empty-state-create"
            >
              Start something
            </button>
          </div>
        </div>
      )}

      {/* Pin count indicator */}
      <div className="absolute bottom-4 left-4 z-[1000]">
        <div className="px-3 py-1.5 rounded-full text-xs shadow-md"
          style={{ backgroundColor: 'var(--fy-surface-card)', color: 'var(--fy-text-secondary)' }}>
          {pins.length} {pins.length === 1 ? 'activity' : 'activities'}
        </div>
      </div>

      {/* Guest banner */}
      {!user && (
        <div className="absolute bottom-20 right-4 z-[1000]">
          <button
            onClick={() => navigate('/auth?mode=signin')}
            className="px-4 py-2 rounded-full text-sm font-medium shadow-lg"
            style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
            data-testid="map-join-btn"
          >
            Join to see more
          </button>
        </div>
      )}
    </div>
  );
}
